/**
 *  @author: Maximilian Gertz
 *  @email: mgertz@nanocosmos.net
 *  @organization: Nanocosmos GmbH
 *  @version: (introduced in analytics-front) 2.26.0
 */

// React Imports
import React from 'react';
import { PropTypes } from 'prop-types';
import { CardTitle, Col, Row } from 'reactstrap';
import ReactDOMServer from 'react-dom/server';
import { Tooltip as ReactTooltip } from 'react-tooltip/dist/react-tooltip.umd';

// Redux Imports
import { useSelector } from 'react-redux';

// Custom Imports
import {
   AlignedIconContainer,
   HeightSetContainer,
   PaddingContainer,
   SimpleFlexItem,
   SimpleFlexRow,
   StandardContainer,
   TwoSideFlexContainer,
   WidgetBodyContainer,
   WidgetContainer,
   WidgetHeaderContainer,
   Description,
   ValueText,
} from '../../../../../../styledComponents/styledComponents';
import { DetailTile, TileContainer } from '../../../Styles';
import { E2E } from '../../../../../../constants/general';
import { ALERT_MAPPER } from './AlertDetailsMapper';
import TextWithToolTip from '../../../TextWithToolTip';
import { troubleModeSelector } from '../../../../../../redux/selectors/selectors';
import AlertDetailsPanel from './AlertDetailsPanel';
import { CHRONOLOGICAL_DATE_TIME_SHORT_FORMAT } from '../../../constants';
import moment from 'moment';

function getStreamDetailsValues(streamDetails, connectionProp) {
   return streamDetails[connectionProp];
}

function DuplicatedIngestWidget({ selectedAlert, height }) {
   const codes = selectedAlert.codes;
   const ALERT_CODE = codes.sort((a, b) => b - a)[0]; // get highest code
   const { title, alertInfo, connectionProp, custom } = ALERT_MAPPER[ALERT_CODE];

   // --------------------------
   // ## React useSelector hooks
   // --------------------------

   const mode = useSelector(troubleModeSelector);
   return (
      <>
         <PaddingContainer>
            <WidgetContainer $margin={'0px 0px 0px 0px'}>
               <WidgetHeaderContainer>
                  <HeightSetContainer $heightInPx={23}>
                     <TwoSideFlexContainer>
                        <SimpleFlexRow></SimpleFlexRow>
                        <SimpleFlexRow>
                           <SimpleFlexItem>
                              <AlignedIconContainer
                                 data-tooltip-id='DI'
                                 data-tooltip-html={ReactDOMServer.renderToStaticMarkup(<div>{alertInfo}</div>)}
                                 className='bi bi-info-circle ml-1'
                              ></AlignedIconContainer>
                              <ReactTooltip
                                 id='DI'
                                 place={'top'}
                                 className={'reduced-line-height bring-on-top'}
                                 backgroundColor={'rgba(0,0,0,0.9)'}
                                 textColor={'rgb(240,240,240)'}
                                 style={{ textAlign: 'center' }}
                                 clickable
                              />
                           </SimpleFlexItem>
                        </SimpleFlexRow>
                     </TwoSideFlexContainer>
                  </HeightSetContainer>
               </WidgetHeaderContainer>
               <WidgetBodyContainer>
                  <HeightSetContainer $heightInPx={height}>
                     <Row>
                        <Col>
                           <CardTitle className='mb-0'>{title}</CardTitle>
                           <div className='small text-muted'>{`From ${moment
                              .utc(selectedAlert.first)
                              .format(CHRONOLOGICAL_DATE_TIME_SHORT_FORMAT)} to ${moment
                              .utc(selectedAlert.last)
                              .format(CHRONOLOGICAL_DATE_TIME_SHORT_FORMAT)} \u00A0\u00A0( UTC time )`}</div>
                        </Col>
                     </Row>
                     <StandardContainer $paddingPx={20}>
                        {ALERT_CODE === 24102 || ALERT_CODE === 24103 ? (
                           <TileContainer gapPx={20} isFullScreen={mode !== E2E}>
                              {getStreamDetailsValues(selectedAlert.streamDetails, connectionProp).map(
                                 (propValue, index) => (
                                    <DetailTile key={propValue} gapPx={20}>
                                       <Description marginLeftPx={0}>{`${index + 1}. ${custom.propLabel}`}</Description>
                                       <ValueText>
                                          <TextWithToolTip text={propValue} idName={'message'} />
                                       </ValueText>
                                    </DetailTile>
                                 ),
                              )}
                           </TileContainer>
                        ) : (
                           <></>
                        )}
                     </StandardContainer>
                     <AlertDetailsPanel selectedAlert={selectedAlert} />
                  </HeightSetContainer>
               </WidgetBodyContainer>
            </WidgetContainer>
         </PaddingContainer>
      </>
   );
}

DuplicatedIngestWidget.propTypes = {
   selectedAlert: PropTypes.object,
   height: PropTypes.number,
};

DuplicatedIngestWidget.defaultProps = {
   height: 300,
};

export default DuplicatedIngestWidget;
